
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { ILead, IDropdown } from "../../models/BaseModel";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useEffect, useState } from "react";
import { baseUrl, Fetch } from "../../shared/Fetch";
import { JVDropdown } from "../../shared/JVDropdown";
import { FileUpload } from "primereact/fileupload";

const schema = yup.object({
    onBehalfUserID: yup.number().required("User is required").min(1, "User is required"),
});

interface Iprops {
    singleData: ILead;
    open: boolean;
    hideDialog: () => void;
    toast: any;
    reloadData: () => void;
    hasPermission: (pageName: string, featureName?: string) => boolean;
}

export const BulkUploadForm = (props: Iprops) => {
    const [users, setUsers] = useState<IDropdown[]>();
    const [resumeFile, setResumeFile] = useState<File | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const { control, handleSubmit, formState: { errors }, reset } = useForm<ILead>({
        resolver: yupResolver(schema),
        defaultValues: props.singleData,
        shouldFocusError: false,
    });

    useEffect(() => {
        loadUsers();
    }, []);

    const loadUsers = async () => {
        const response = await Fetch("user/dropdown", "GET", null, null, null);
        if (response) {
            setUsers(await response.json());
        }
    };

    const saveToDB = async (data: any) => {
        setSubmitting(true);

        const formData = new FormData();
        formData.append("file", resumeFile as Blob);
        formData.append("user", JSON.stringify(data));

        let duplicateBlob: Blob | null = null;

        try {
            const response = await fetch(`${baseUrl}Contact_Person/importContact`, {
                method: "POST",
                headers: {
                    Authorization: `Bearer ${JSON.parse(sessionStorage.getItem("auth") + "")?.token}`,
                },
                body: formData,
            });

            const importStatus = response.headers.get("X-Import-Status");
            const contentType = response.headers.get("Content-Type");

            // DUPLICATE FILE
            if (contentType && contentType.includes("application/vnd.openxmlformats-officedocument.spreadsheetml.sheet")) {

                duplicateBlob = await response.blob();

                const isAllDuplicate = importStatus?.toLowerCase().includes("all values are duplicate");

                props.toast.current.show({
                    severity: isAllDuplicate ? "error" : "warn",
                    summary: isAllDuplicate ? "All Entries Are Duplicates" : "Partial Duplicates Found",
                    detail: isAllDuplicate
                        ? "No new contacts were added because all entries already exist."
                        : "Some contacts were skipped because they already exist in the system.",
                    sticky: true,
                    content: (
                        <div className="flex flex-column gap-2">
                            <span>
                                {isAllDuplicate
                                    ? "All uploaded contacts are duplicates."
                                    : "Some duplicates were found in your file."}
                            </span>

                            <Button
                                label="Download Duplicate Report"
                                icon="pi pi-download"
                                type="button"
                                className="p-button-text p-button-sm"
                                onClick={() => {
                                    if (duplicateBlob) {
                                        const url = window.URL.createObjectURL(duplicateBlob);
                                        const a = document.createElement("a");
                                        a.href = url;
                                        a.download = `DuplicateContacts_${new Date()
                                            .toISOString()
                                            .replace(/[:.]/g, "-")}.xlsx`;
                                        document.body.appendChild(a);
                                        a.click();
                                        a.remove();
                                    }
                                }}
                            />
                        </div>
                    ),
                });

                onHide();
                props.reloadData();
            }
            else {
                const result = await response.json();

                if (response.ok) {
                    props.toast.current.show({
                        severity: "success",
                        summary: "Upload Successful",
                        detail: result.message || "All contacts uploaded successfully.",
                        life: 4000,
                    });

                    onHide();
                    props.reloadData();
                }
                else {
                    props.toast.current.show({
                        severity: "error",
                        summary: "Upload Failed",
                        detail: result.message || "The upload failed.",
                        life: 4000,
                    });
                }
            }
        }
        catch (error) {
            console.error("Error uploading:", error);

            props.toast.current.show({
                severity: "error",
                summary: "Error",
                detail: "Unexpected error occurred during upload.",
                life: 4000,
            });
        }

        setSubmitting(false);
    };

    const onHide = () => {
        if (submitting) return;

        props.hideDialog();

        reset({
            onBehalfUserID: 0,
        });

        setResumeFile(null);
    };

    function onSubmit(data: ILead) {
        if (!resumeFile) {
            props.toast.current.show({
                severity: "error",
                detail: "Please Upload Excel First!",
                life: 3000,
            });
            return;
        }

        saveToDB(data);
    }

    const onUpload = (event: any) => {
        const file = event.files[0];

        const allowedExtensions = [".xls", ".xlsx"];
        const ext = file.name.substring(file.name.lastIndexOf(".")).toLowerCase();

        if (!allowedExtensions.includes(ext)) {
            props.toast.current.show({
                severity: "error",
                detail: "Please upload a valid Excel file (.xls or .xlsx).",
            });
            event.options.clear();
            return;
        }

        if (file.size > 1024 * 1024 * 5) {
            props.toast.current.show({
                severity: "error",
                detail: "File size should be less than 5 MB.",
            });
            event.options.clear();
            return;
        }

        setResumeFile(file);

        props.toast.current.show({
            severity: "success",
            summary: "Success",
            detail: "Excel file selected",
            life: 3000,
        });

        event.options.clear();
    };

    return (
        <Dialog
            visible={props.open}
            style={{ width: "400px" }}
            header="Bulk Upload"
            modal
            className="p-fluid"
            onHide={() => {
                if (!submitting) {
                    onHide();
                }
            }} closable={!submitting}
            closeOnEscape={!submitting}
            dismissableMask={!submitting}
        >

            {submitting && (
                <div
                    style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: "rgba(255,255,255,0.7)",
                        zIndex: 10,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    }}
                >
                    <i className="pi pi-spin pi-spinner" style={{ fontSize: "2rem" }} />
                </div>
            )}

            <form onSubmit={handleSubmit(onSubmit)} className="p-fluid formgrid grid">

                <JVDropdown
                    column={1}
                    label="On Behalf Users"
                    control={control}
                    options={users}
                    optionLabel="name"
                    optionValue="id"
                    name="onBehalfUserID"
                    placeholder="Select"
                    error={errors.onBehalfUserID?.message}
                    disabled={submitting}
                />

                <div className="card flex justify-content-center ml-3">
                    <FileUpload
                        chooseLabel="Choose Excel File"
                        mode="basic"
                        name="file"
                        accept=".xls,.xlsx"
                        customUpload
                        uploadHandler={onUpload}
                        auto
                        disabled={submitting}
                    />
                </div>

                <div className="col-12 md:col-12 flex gap-4 justify-content-end">

                    <Button
                        style={{ width: "100px" }}
                        label="SAVE"
                        type="submit"
                        className="p-button-rounded"
                        loading={submitting}
                        disabled={submitting}
                    />

                    <Button
                        style={{ width: "100px" }}
                        label="CLOSE"
                        onClick={onHide}
                        type="button"
                        className="p-button-rounded p-button-outlined surface-200"
                        disabled={submitting}
                    />

                </div>
            </form>
        </Dialog>
    );
};
